import { Component, computed, effect, inject, input } from '@angular/core'
import { MaintenanceService } from './maintenance.service'

/**
 * Banner globale di manutenzione (stile GitLab).
 * - In layout (default) si ancora sotto l'header (top: 37px) e la classe su
 *   <body> fa scorrere sidebar/contenuto (regole in src/styles.css).
 * - Con isTop=true (pagine senza header, es. login) si ancora a top: 0.
 * Altezza fissa 40px, messaggio su una riga con ellipsis (testo completo nel title).
 */
@Component({
  selector: 'fq-maintenance-banner',
  standalone: true,
  template: `
    @if (visible()) {
      <div class="fq-mbanner"
        [class.is-top]="isTop()"
        [style.background]="svc.bannerColor()"
        [style.color]="textColor()"
        [title]="svc.state()?.message ?? ''"
        role="alert">
        <span class="fq-mbanner-icon" aria-hidden="true">{{ svc.phase() === 'active' ? '⛔' : '📅' }}</span>
        <span class="fq-mbanner-msg">{{ svc.state()?.message }}</span>
        @if (badgeText(); as badge) {
          <span class="fq-mbanner-tag">{{ badge }}</span>
        }
        @if (svc.formatCountdown(); as countdown) {
          <span class="fq-mbanner-count">{{ svc.phase() === 'scheduled' ? 'inizia tra' : 'termine stimato tra' }} <b>{{ countdown }}</b></span>
        }
        <button type="button" class="fq-mbanner-close" aria-label="Chiudi" (click)="svc.dismissBanner()">✕</button>
      </div>
    }
  `
})
export class MaintenanceBannerComponent {
  readonly svc = inject(MaintenanceService)

  /** true = ancorato a top:0 (pagine senza header). */
  readonly isTop = input(false)

  readonly visible = computed(() => this.svc.showBanner() && !this.svc.bannerDismissed())

  readonly textColor = computed(() => this.pickTextColor(this.svc.bannerColor()))

  /** Testo del badge: custom se impostato, altrimenti default solo in fase attiva. */
  readonly badgeText = computed(() => {
    const state = this.svc.state()
    if (!state) {
      return null
    }
    if (state.badge) {
      return state.badge
    }
    return this.svc.phase() === 'active' ? 'MANUTENZIONE IN CORSO' : null
  })

  constructor() {
    effect((onCleanup) => {
      const active = this.visible() && !this.isTop()
      document.body.classList.toggle('fq-maintenance-banner', active)
      onCleanup(() => document.body.classList.remove('fq-maintenance-banner'))
    })
  }

  /** Nero su colori chiari, bianco su colori scuri (luminanza percepita). */
  private pickTextColor (hex: string): string {
    return pickBannerTextColor(hex)
  }
}

/** Colore testo leggibile su uno sfondo esadecimale (luminanza percepita). */
export function pickBannerTextColor (hex: string): string {
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex)
  if (!match) {
    return '#ffffff'
  }
  let value = match[1]
  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('')
  }
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.62 ? '#111827' : '#ffffff'
}
